import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import BaseCard from "../../components/ui/BaseCard";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseButton from "../../components/ui/BaseButton";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";

import { fetchQualificationTypes } from "../../services/api/qualification";
import { debounce } from "../../utils/helpers";

import AddEditQualificationModal from "./modals/AddEditQualificationModal";

const menuOptions = [];

export default function QualificationsTable() {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);

  const [isQualModalOpen, setQualModalOpen] = useState(false);

  const params = useMemo(
    () => ({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search: searchQuery || undefined,
    }),
    [paginationModel, searchQuery]
  );

  const {
    data: qualifications,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["qualifications", params],
    queryFn: () => fetchQualificationTypes(params),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (qualifications?.data?.total !== undefined) {
      setRowCount(qualifications?.data?.total);
    }
  }, [qualifications]);

  const qualificationsList = useMemo(
    () => qualifications?.data?.data ?? [],
    [qualifications]
  );

  const debouncedSetSearchQuery = useRef(
    debounce((val) => {
      setSearchQuery(val);
      setPaginationModel((m) => ({ ...m, page: 0 }));
    }, 400)
  );

  const handleSearch = (e) => {
    setSearchInput(e.target.value);
    debouncedSetSearchQuery.current(e.target.value);
  };

  const columns = [
    {
      field: "qualification",
      headerName: "Qualification Type",
      flex: 1,
      renderCell: (params) => (
        <button className="text-primary">{params?.row?.name}</button>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={
            <i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base" />
          }
          onItemClick={(item) => {}}
        />
      ),
    },
  ];

  return (
    <>
      <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
        <div className="flex justify-between items-center w-full gap-4">
          <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary"><i className="fa-light fa-filter text-white text-sm"></i></span>
            <p className="text-primary ml-2">Add Filter</p>
          </div>
          <div className="w-[76%]">
            <BaseTextField
              placeholder="Search qualifications"
              value={searchInput}
              onChange={handleSearch}
              startAdornment={
                <i className="fa-light fa-magnifying-glass text-lg" />
              }
            />
          </div>
          <BaseButton
            className="md:w-auto w-full"
            variant="contained"
            color="primary"
            style={{ padding: "6px 12px" }}
            onClick={() => setQualModalOpen(true)}
          >
            Create New Qualification
          </BaseButton>
        </div>
      </div>

      <BaseCard
        className="rounded-tl-xl rounded-tr-xl shadow-lg mt-5"
        elevation={0}
      >
            <BaseDataTableV2
              className="max-h-[calc(100vh-320px)] overflow-auto"
              columns={columns}
              data={qualificationsList}
              checkboxSelection
              isLoading={isPending}
              hideFooter={false}
              pagination
              paginationMode="server"
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 20, 50, 100]}
            />
      </BaseCard>
      {isQualModalOpen && (
        <AddEditQualificationModal
          open={isQualModalOpen}
          close={() => setQualModalOpen(false)}
          refetchQualifications={refetch}
        />
      )}
    </>
  );
}
